
import { defineComponent } from 'vue'

import OwnerBillingMixin from '@/mixins/ownerBillingMixin'
import TeamDetailMixin from '@/mixins/teamDetailMixin'

import { resolveNodes } from '~/utils/array'

import { OwnerBillingVersion, TeamMemberRoleChoices } from '~/types/types'

interface member {
  index: number
  email: string
  isValid: boolean
  modifyAllowed: boolean
  role: TeamMemberRoleChoices
}

const INPUT_DATA: member[] = [
  {
    index: 0,
    email: '',
    isValid: true,
    modifyAllowed: false,
    role: TeamMemberRoleChoices.Contributor
  },
  {
    index: 1,
    email: '',
    isValid: true,
    modifyAllowed: false,
    role: TeamMemberRoleChoices.Contributor
  },
  {
    index: 2,
    email: '',
    isValid: true,
    modifyAllowed: false,
    role: TeamMemberRoleChoices.Contributor
  }
]

const ROLES = [
  {
    value: TeamMemberRoleChoices.Admin,
    label: 'Administrator'
  },
  {
    value: TeamMemberRoleChoices.Member,
    label: 'Member'
  },
  {
    value: TeamMemberRoleChoices.Contributor,
    label: 'Contributor'
  }
]

export default defineComponent({
  name: 'InviteMembersModal',
  mixins: [TeamDetailMixin, OwnerBillingMixin],
  data() {
    return {
      showModal: false,
      membersToInvite: [] as member[],
      showSuccessModal: false,
      roles: ROLES
    }
  },
  async fetch(): Promise<void> {
    const { owner, provider } = this.$route.params
    const args = { login: owner, provider }

    // Set the invite members list before queries to ensure
    // consistency between states
    this.membersToInvite = this.initialMembersToInvites
    await Promise.all([
      this.fetchInviteUrl(args),
      ...(this.isUnifiedBilling ? [] : [this.fetchSeatsInfo(args)])
    ])
    this.membersToInvite = this.initialMembersToInvites
  },
  methods: {
    async resetInviteLink(): Promise<void> {
      await this.resetInviteUrl({
        ownerId: this.team.id
      })
      this.$toast.success('Invitation link successfully reset')
    },

    areEmailsInvalid(): boolean {
      this.membersToInvite = this.membersToInvite.map((member) => {
        if (member.email) {
          return {
            index: member.index,
            email: member.email,
            role: member.role,
            modifyAllowed: member.modifyAllowed,
            isValid: this.validEmail(member.email)
          }
        }
        return member
      })

      return this.membersToInvite.map((member) => member.isValid).some((el) => el === false)
    },
    validEmail(email: string): boolean {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(email)
    },

    async sendInvite(): Promise<void> {
      if (this.areEmailsInvalid()) {
        this.$toast.danger('Please enter a valid email')
        return
      }

      const { owner, provider } = this.$route.params
      const refetchArgs = {
        login: owner,
        provider,
        currentPage: 1,
        limit: 10
      }

      const invitees = this.membersToInvite
        .filter((member) => {
          return member.email !== ''
        })
        .map((member) => {
          return {
            email: member.email,
            role: member.role
          }
        })
      try {
        await this.inviteAll({
          ownerId: this.team.id,
          invitees,
          ...refetchArgs
        })
        this.membersToInvite = this.initialMembersToInvites
        this.showSuccessModal = true
        this.$emit('inviteSuccess')
        this.close()
      } catch (e) {
        this.$toast.danger((e as Error).message.replace('GraphQL error: ', ''))
      }
    },

    addMore(): void {
      const modifyAllowed = this.seatsLeftCount - this.membersToInvite.length > 0 ? true : false
      this.membersToInvite.push({
        index: this.membersToInvite.length + 1,
        email: '',
        isValid: true,
        modifyAllowed,
        role: modifyAllowed ? TeamMemberRoleChoices.Member : TeamMemberRoleChoices.Contributor
      })
    },

    openInviteModal(): void {
      this.membersToInvite = this.initialMembersToInvites
      this.showSuccessModal = false
      this.showModal = true
    },

    close(): void {
      this.membersToInvite = this.initialMembersToInvites
      this.showModal = false
    }
  },
  computed: {
    isUnifiedBilling(): boolean {
      return this.owner?.billingVersion === OwnerBillingVersion.V2
    },

    disableSend(): boolean {
      return (
        this.membersToInvite.filter((member) => {
          return member.email !== ''
        }).length < 1
      )
    },
    pendingInvites(): number {
      let count = 0
      const invitees = resolveNodes(this.team.invites)
      invitees.forEach((invitee) => {
        if (invitee?.role !== TeamMemberRoleChoices.Contributor) {
          count += 1
        }
      })
      return count
    },
    seatsLeftCount(): number {
      if (this.isUnifiedBilling) {
        return Infinity
      }
      const { seatsTotal, seatsUsed } = this.ownerBillingInfo
      if (seatsTotal && seatsUsed) {
        return seatsTotal - seatsUsed - this.pendingInvites
      }
      return 0
    },
    initialMembersToInvites(): member[] {
      let seatsAvailable = this.seatsLeftCount
      return INPUT_DATA.map((member) => {
        if (seatsAvailable-- > 0) {
          return {
            ...member,
            role: TeamMemberRoleChoices.Member,
            modifyAllowed: true
          }
        } else {
          return member
        }
      })
    }
  }
})
